import { format, parseISO, isValid } from "date-fns"
/**
 * Formater une isoDate au format "DD/MM/YYYY à HH:mm"
 * @param {string} isoDate à formater
 * @returns {string} Chaine de charactère d'une date au format "DD/MM/YYYY à HH:mm"
 */
export function getLocaleDateAndHourFromIso(isoDate: string | null): string {
  if (!isoDate) return ""

  const date = new Date(isoDate)
  const formattedDate = date.toLocaleDateString()
  const formattedTime = `${date.getHours() < 10 ? "0" : ""}${date.getHours()}:${
    date.getMinutes() < 10 ? "0" : ""
  }${date.getMinutes()}`

  return formattedDate === "Invalid Date" ? isoDate : `${formattedDate} à ${formattedTime}`
}

/**
 * Formater une isoDate au format ISO 8601 "YYYY-MM-DD"
 * @param {string} isoDate à formater
 * @returns {string} Chaine de charactère d'une date au format "YYYY-MM-DD"
 */
export function getLocaleDateFromIso(isoDate: string | null): string {
  if (!isoDate) return ""

  const date = new Date(isoDate)
  // Vérifiez si la date est valide avant de la formater
  if (isNaN(date.getTime())) {
    return isoDate
  }

  // Utilisez la méthode toISOString() pour obtenir la date au format ISO 8601 et
  // utilisez ensuite slice() pour obtenir uniquement la partie date (YYYY-MM-DD)
  const formattedDate = date.toISOString().slice(0, 10)

  return formattedDate
}

type ListType = string | number | boolean

/**
 * Vérifier si une value est de type string et si c'est une isoDate
 * @param {ListType} value value à vérifier.
 * @returns {boolean} true, la value est une isoDate.
 */
export function isISODateString(value: ListType): boolean {
  if (typeof value === "string") {
    const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/
    return isoDateRegex.test(value)
  }

  return false
}

/**
 * Formater une isoDate au format "DD/MM/YYYY"
 * @param {string} isoDate à formater
 * @returns {string} Chaine de charactère d'une date au format "DD/MM/YYYY"
 */
export function formatIsoDateToDateFormat(isoDate: string): string {
  try {
    const [year, month, day] = isoDate.split("-")
    return `${day}/${month}/${year}`
  } catch (e) {
    return ""
  }
}

/**
 * Formater une date au format du dateFormat fourni.
 *
 * Normalise l'entrée en `Date` (`parseISO` pour les chaînes ISO — date-fns v2 n'accepte
 * plus de string en argument) puis VALIDE avant d'appeler `format` : une date invalide
 * (ex. une valeur non-date comme `"01130"` ou une chaîne mal formée) ne lève plus de
 * `RangeError: Invalid time value` — la valeur d'origine est retournée telle quelle.
 *
 * @param {string | number | Date} date à formater
 * @param {string} dateFormat patron de format (défaut `yyyy-MM-dd`)
 * @returns {string} la date formatée, ou la valeur d'origine si elle n'est pas une date valide
 */
export function getFormatDate(date: string | number | Date | null | undefined, dateFormat: string): string {
  if (date === null || date === undefined || date === "") return ""

  const parsed =
    typeof date === "string" ? parseISO(date) : date instanceof Date ? date : new Date(date)

  // Garde-fou : ne jamais passer une date invalide à format() (sinon RangeError).
  if (!isValid(parsed)) return `${date}`

  try {
    return format(parsed, dateFormat || "yyyy-MM-dd")
  } catch (e) {
    console.error(e)
    return `${date}`
  }
}

export function getInfosDate(isoDate: string | null) {
  if (!isoDate) return ""

  const date = new Date(isoDate)

  const year = date?.getUTCFullYear()
  const month = date?.getUTCMonth()
  const day = date?.getUTCDate()

  return { year, month, day }
}

/**
 * Formats acceptés à la SAISIE d'une date dans un react-datepicker. Le premier élément reste le
 * format du champ, celui de l'affichage (react-datepicker affiche avec `dateFormat[0]`). Suivent :
 * - le format sans son heure (« dd-MM-yyyy HH:mm » → « dd-MM-yyyy ») : une date seule reste acceptée ;
 * - l'année sur 2 chiffres (`yyyy` → `yy`) : « 10-05-26 » reste le 10 mai 2026 ;
 * - pour chacun, les variantes à « - », « / » et « . » quel que soit le séparateur configuré
 *   (même ordre jour / mois / année).
 *
 * Pourquoi : avec un format unique, une saisie qui ne le respecte pas (« 10/05/2026 » pour
 * `dd-MM-yyyy`) retombe sur `new Date(saisie)`, qui lit le format US mm/jj/aaaa — 10 mai devient
 * 5 octobre, et « 25/12/2026 » est rejeté (GC-333). Avec un tableau, react-datepicker n'essaie que
 * ces formats ; mais il perd aussi sa lecture d'une saisie partielle du format, qui acceptait la date
 * sans l'heure et l'année sur 2 chiffres : d'où les deux variantes ci-dessus.
 */
export function getDateInputFormats(dateFormat: string): string[] {
  const withoutTime = dateFormat.replace(/\s+[Hh].*$/, "")
  const bases = withoutTime && withoutTime !== dateFormat ? [dateFormat, withoutTime] : [dateFormat]
  const withShortYear = bases.flatMap(f => (f.includes("yyyy") ? [f, f.replace("yyyy", "yy")] : [f]))
  const withSeparators = withShortYear.flatMap(f => [f, ...["-", "/", "."].map(sep => f.replace(/[-/.]/g, sep))])
  return Array.from(new Set(withSeparators))
}
